import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { STORAGE_KEY } from "../features/api/apiSlice";

type Props = {
  roles?: string[];
  perms?: string[];
  children: React.ReactNode;
};

function normalizeRoles(input: any): string[] {
  if (!input) return [];

  const arr = Array.isArray(input) ? input : [];
  // A role is either a bare name or a spatie object { name, permissions } —
  // the API sends objects so the sidebar can tell one role's privileges from
  // another's. String()-ing the object gives "[object object]", which matches
  // no gate and would lock a super_admin out of every role-gated route.
  const normalized = arr
    .map((r) => String(typeof r === "string" ? r : r?.name ?? "").toLowerCase().trim())
    .filter(Boolean);

  // legacy mapping used in your older routes
  return normalized.map((r) => {
    if (r === "user") return "member";
    if (r === "staff") return "executive"; // adjust if staff should mean member
    return r;
  });
}

function uniqueStrings(values: string[]): string[] {
  const seen: Record<string, true> = {};
  const out: string[] = [];
  for (let i = 0; i < values.length; i++) {
    const k = String(values[i] || "")
      .toLowerCase()
      .trim();
    if (!k) continue;
    if (!seen[k]) {
      seen[k] = true;
      out.push(k);
    }
  }
  return out;
}

export default function RequirePermission({ roles = [], perms = [], children }: Props) {
  const location = useLocation();

  const raw = localStorage.getItem(STORAGE_KEY);
  const auth = raw ? JSON.parse(raw) : null;

  const token = auth?.token;

  // ✅ Not logged in
  if (!token) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // ✅ Route has no restriction
  if (!roles.length && !perms.length) return <>{children}</>;

  // ✅ Your STORAGE_KEY stores roles/whois at ROOT (flattened)
  const whois = String(auth?.whois || "")
    .toLowerCase()
    .trim();
  const spatieRoles = normalizeRoles(auth?.roles);

  const effectiveRoles = uniqueStrings(
    spatieRoles.concat(whois ? [whois] : [])
  );

  // super_admin bypasses all role restrictions
  const isSuperAdmin =
    effectiveRoles.includes("super_admin") ||
    effectiveRoles.includes("super admin");

  // User's granted permissions (login stores them at the root, as strings).
  const userPerms = uniqueStrings(
    (Array.isArray(auth?.permissions) ? auth.permissions : []).map((p: any) =>
      typeof p === "string" ? p : p?.name
    )
  );

  // When both roles and perms are given they must BOTH be satisfied
  // (e.g. admin AND upgrade_applications.access). A missing list is not a gate.
  const roleOk =
    roles.length === 0 ||
    roles.some((r) => effectiveRoles.includes(String(r).toLowerCase().trim()));
  const permOk =
    perms.length === 0 ||
    perms.some((p) => userPerms.includes(String(p).toLowerCase().trim()));

  const allowed = isSuperAdmin || (roleOk && permOk);

  if (!allowed) return <Navigate to="/unauthorized" replace />;

  return <>{children}</>;
}
